import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PromoBanner from "@/components/PromoBanner";
import { ChannelBadge, CHANNELS } from "@/components/channels";
import {
  Sparkles, Wand2, Users, Video, Languages, Copy, Eraser, Crop, Images, UserCheck,
  Share2, Calendar, ShieldCheck, BarChart3, Gauge, Zap, ArrowRight, Star, Check, TrendingUp,
} from "lucide-react";

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1739980737820-b6bb1a9b8456?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxlY29tbWVyY2UlMjBwcm9kdWN0JTIwcHJlc2VudGF0aW9uJTIwbHV4dXJ5JTIwYmVhdXR5fGVufDB8fHx8MTc5MTE3MjM1Mnww&ixlib=rb-4.1.0&q=85",
  model1: "https://images.unsplash.com/photo-1617690032354-34273b431955?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHw0fHxmYXNoaW9uJTIwbW9kZWwlMjBzdHVkaW8lMjBhZCUyMGhpZ2glMjBmYXNoaW9ufGVufDB8fHx8MTc5MTE3MjM1Mnww&ixlib=rb-4.1.0&q=85",
  model2: "https://images.unsplash.com/photo-1715541448446-3369e1cc0ee9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxmYXNoaW9uJTIwbW9kZWwlMjBzdHVkaW8lMjBhZCUyMGhpZ2glMjBmYXNoaW9ufGVufDB8fHx8MTc5MTE3MjM1Mnww&ixlib=rb-4.1.0&q=85",
  tech: "https://images.unsplash.com/photo-1676902496097-c5a9c7dc64f9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxtaW5pbWFsJTIwdGVjaCUyMHdlYXJhYmxlJTIwbHV4dXJ5JTIwd2F0Y2glMjBoZWFkcGhvbmV8ZW58MHx8fHwxNzkxMTcyMzU5fDA&ixlib=rb-4.1.0&q=85",
  neon: "https://images.unsplash.com/photo-1528826792843-696806ff0b87?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBlY29tbWVyY2UlMjBwcm9kdWN0JTIwc2hvd2Nhc2UlMjBuZW9uJTIwZ2xvd2luZ3xlbnwwfHx8fDE3OTExNzIzNTl8MA&ixlib=rb-4.1.0&q=85",
};

const STUDIO_FEATURES = [
  { Icon: Wand2, title: "Visuels IA génératifs", desc: "Fonds virtuels, ambiances studio et mises en scène réalistes." },
  { Icon: Users, title: "Mannequins virtuels", desc: "Avatars diversifiés portant vos vêtements et accessoires." },
  { Icon: Video, title: "Vidéos Reels / TikTok", desc: "Montage auto avec zoom, transitions et sous-titres animés." },
  { Icon: Languages, title: "Copies multilingues", desc: "Accroches adaptées à chaque culture et pays, A/B testing." },
  { Icon: Copy, title: "Variantes illimitées", desc: "Des dizaines de variations d'une pub en un seul clic." },
  { Icon: Eraser, title: "Détourage magique", desc: "Suppression et remplacement d'arrière-plan instantanés." },
  { Icon: Crop, title: "Formats natifs", desc: "1:1, 9:16 et 16:9 ajustés à chaque réseau automatiquement." },
  { Icon: Images, title: "Carrousels storytelling", desc: "Suites d'images cohérentes pour raconter votre marque." },
  { Icon: UserCheck, title: "UGC simulé", desc: "Visuels au style authentique « créateur de contenu »." },
];

const HUB_FEATURES = [
  { Icon: Share2, title: "Diffusion multi-réseaux", desc: "Poussez une pub optimisée sur 4 réseaux en une validation." },
  { Icon: Calendar, title: "Planificateur intelligent", desc: "Calendrier éditorial visuel pour programmer vos lancements." },
  { Icon: ShieldCheck, title: "Pré-validation conformité", desc: "L'IA vérifie les politiques pub pour éviter les blocages." },
];
const ANALYTICS_FEATURES = [
  { Icon: BarChart3, title: "ROAS, CPC, CPA unifiés", desc: "Suivi centralisé du CA généré et de vos performances." },
  { Icon: Gauge, title: "Score prédictif IA", desc: "Notation de chaque visuel avant même son lancement." },
  { Icon: TrendingUp, title: "Optimisation temps réel", desc: "Coupez les pubs faibles, boostez celles qui performent." },
];

const CASES = [
  { brand: "Nova Mode", sector: "Mode", roas: "+312%", img: IMAGES.model1, quote: "Nos créations publicitaires sont 10x plus rapides à produire." },
  { brand: "Lumen Beauty", sector: "Beauté", roas: "+248%", img: IMAGES.hero, quote: "Le score IA nous fait économiser des milliers en tests." },
  { brand: "Pulse Tech", sector: "High-Tech", roas: "+187%", img: IMAGES.tech, quote: "La diffusion multi-réseaux en un clic a changé notre workflow." },
];

const FAQS = [
  { q: "Comment fonctionne la génération par IA ?", a: "Vous importez vos produits, choisissez un format et un réseau, puis notre IA générative crée des visuels et des textes publicitaires optimisés pour la conversion en quelques secondes." },
  { q: "Puis-je diffuser directement sur les réseaux ?", a: "Oui. Le Hub de diffusion vous permet d'exporter et de programmer vos créations vers Meta, TikTok, Google, Pinterest et Snapchat, avec un calendrier éditorial centralisé." },
  { q: "Qu'est-ce que les crédits IA ?", a: "Chaque génération consomme des crédits. Vous recevez 150 crédits offerts à l'inscription, puis un quota mensuel selon votre abonnement (Starter, Pro, Scale, Agence)." },
  { q: "Mes données sont-elles sécurisées ?", a: "Absolument. Vos catalogues et données bancaires sont chiffrés et conformes au RGPD. Un journal d'activités trace toutes les actions sur votre compte." },
  { q: "Puis-je annuler mon abonnement ?", a: "Oui, à tout moment depuis votre portail de facturation sécurisé. Aucun engagement de durée." },
];

function AnimatedCounter({ target, suffix = "" }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf; const start = Date.now(); const dur = 1600;
    const tick = () => {
      const p = Math.min((Date.now() - start) / dur, 1);
      setVal(Math.floor(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return <span>{val.toLocaleString("fr-FR")}{suffix}</span>;
}

function InteractiveDemo() {
  const formats = [{ id: "1:1", label: "Carré" }, { id: "9:16", label: "Story / Reel" }, { id: "16:9", label: "Paysage" }];
  const [fmt, setFmt] = useState("1:1");
  const [rendering, setRendering] = useState(false);
  const imgs = { "1:1": IMAGES.neon, "9:16": IMAGES.model2, "16:9": IMAGES.tech };
  const aspect = { "1:1": "aspect-square", "9:16": "aspect-[9/16] max-w-[220px] mx-auto", "16:9": "aspect-video" };
  const render = (f) => { setFmt(f); setRendering(true); setTimeout(() => setRendering(false), 900); };

  return (
    <div className="mg-glass rounded-3xl p-5 mg-glow">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Sparkles size={14} className="text-primary" /> Démo live</span>
        <div className="flex gap-1">
          {formats.map((f) => (
            <button key={f.id} onClick={() => render(f.id)} className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${fmt === f.id ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`} data-testid={`demo-format-${f.id}`}>
              {f.id}
            </button>
          ))}
        </div>
      </div>
      <div className={`relative rounded-2xl overflow-hidden border border-border ${aspect[fmt]} transition-all duration-500`}>
        <img src={imgs[fmt]} alt="Publicité générée" className="w-full h-full object-cover" />
        {rendering && (
          <div className="absolute inset-0 bg-background/70 backdrop-blur-sm flex flex-col items-center justify-center gap-3">
            <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            <span className="text-xs text-foreground">Génération IA…</span>
          </div>
        )}
        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
          <span className="px-2 py-1 rounded-lg bg-black/60 backdrop-blur text-white text-[11px] font-medium">Score IA 94/100</span>
          <span className="px-2 py-1 rounded-lg bg-emerald-500/90 text-white text-[11px] font-semibold">Prêt à diffuser</span>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2">
        {Object.keys(CHANNELS).map((id) => <ChannelBadge key={id} id={id} size={32} />)}
      </div>
    </div>
  );
}

const FeatureCard = ({ Icon, title, desc, accent = "indigo" }) => (
  <div className="group p-6 rounded-2xl border border-border bg-card hover:border-primary/40 transition-all hover:-translate-y-1">
    <div className={`h-11 w-11 rounded-xl flex items-center justify-center mb-4 bg-gradient-to-br ${accent === "cyan" ? "from-cyan-500/20 to-cyan-500/5" : accent === "pink" ? "from-pink-500/20 to-pink-500/5" : "from-indigo-500/20 to-indigo-500/5"}`}>
      <Icon size={22} className={accent === "cyan" ? "text-cyan-400" : accent === "pink" ? "text-pink-400" : "text-indigo-400"} />
    </div>
    <h4 className="font-display font-semibold text-lg mb-1.5">{title}</h4>
    <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
  </div>
);

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden mg-radial">
        <div className="absolute inset-0 mg-grid-bg opacity-[0.15] pointer-events-none" />
        <div className="max-w-7xl mx-auto relative">
          <div className="mb-10"><PromoBanner /></div>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="mg-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-sm font-medium mb-6">
              <Sparkles size={15} className="text-primary" /> Nouvelle génération de pub e-commerce
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Créez, personnalisez et diffusez vos <span className="mg-gradient-text">publicités <span className="whitespace-nowrap">e-commerce</span></span> en un clic grâce à l'IA.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">
              Monaga Adflow génère vos visuels, vidéos et textes publicitaires optimisés pour la conversion, puis les diffuse sur tous les réseaux. Pour toutes les marques : mode, beauté, tech, accessoires.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button onClick={() => navigate("/register")} className="h-12 px-7 rounded-xl text-base bg-gradient-to-r from-indigo-500 to-cyan-500 text-white border-0 mg-glow" data-testid="hero-cta-register">
                Démarrer gratuitement <ArrowRight size={18} className="ml-1" />
              </Button>
              <Button onClick={() => navigate("/pricing")} variant="outline" className="h-12 px-7 rounded-xl text-base" data-testid="hero-cta-pricing">
                Voir les tarifs
              </Button>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              <div><p className="font-display text-3xl font-extrabold mg-gradient-text"><AnimatedCounter target={312} suffix="%" /></p><p className="text-xs text-muted-foreground mt-1">ROAS moyen</p></div>
              <div><p className="font-display text-3xl font-extrabold mg-gradient-text"><AnimatedCounter target={50} suffix="K+" /></p><p className="text-xs text-muted-foreground mt-1">Pubs générées</p></div>
              <div><p className="font-display text-3xl font-extrabold mg-gradient-text"><AnimatedCounter target={5} /></p><p className="text-xs text-muted-foreground mt-1">Réseaux connectés</p></div>
            </div>
          </div>
          <div className="mg-float"><InteractiveDemo /></div>
          </div>
        </div>
      </section>

      {/* LOGOS / CHANNELS strip */}
      <section className="px-6 py-8 border-y border-border">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
          <span className="text-sm text-muted-foreground">Diffusez partout :</span>
          {Object.keys(CHANNELS).map((id) => (
            <div key={id} className="flex items-center gap-2"><ChannelBadge id={id} size={30} /><span className="text-sm font-medium">{CHANNELS[id].name}</span></div>
          ))}
        </div>
      </section>

      {/* STUDIO FEATURES */}
      <section id="studio" className="px-6 py-24">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-14">
            <span className="text-xs font-semibold tracking-widest uppercase text-primary">Studio IA de création</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3">Plus de 20 innovations de contenu, propulsées par l'IA.</h2>
            <p className="text-muted-foreground mt-4 text-lg">Un studio complet pour transformer vos produits bruts en publicités haut de gamme prêtes à convertir.</p>
          </div>
          <div id="features" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {STUDIO_FEATURES.map((f, i) => <FeatureCard key={f.title} {...f} accent={["indigo", "cyan", "pink"][i % 3]} />)}
          </div>
        </div>
      </section>

      {/* HUB + ANALYTICS */}
      <section className="px-6 py-10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12">
          <div>
            <span className="text-xs font-semibold tracking-widest uppercase text-cyan-400">Hub de diffusion</span>
            <h3 className="font-display text-2xl font-bold mt-2 mb-6">Pilotez toutes vos campagnes depuis un seul endroit.</h3>
            <div className="space-y-4">{HUB_FEATURES.map((f) => <FeatureCard key={f.title} {...f} accent="cyan" />)}</div>
          </div>
          <div>
            <span className="text-xs font-semibold tracking-widest uppercase text-pink-400">Intelligence prédictive</span>
            <h3 className="font-display text-2xl font-bold mt-2 mb-6">Optimisez vos performances avant même de lancer.</h3>
            <div className="space-y-4">{ANALYTICS_FEATURES.map((f) => <FeatureCard key={f.title} {...f} accent="pink" />)}</div>
          </div>
        </div>
      </section>

      {/* CASES */}
      <section id="cases" className="px-6 py-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs font-semibold tracking-widest uppercase text-primary">Cas clients</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3">Des marques qui performent avec Monaga Adflow.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {CASES.map((c) => (
              <div key={c.brand} className="rounded-2xl border border-border bg-card overflow-hidden group">
                <div className="aspect-[4/3] overflow-hidden"><img src={c.img} alt={c.brand} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div><p className="font-display font-bold">{c.brand}</p><p className="text-xs text-muted-foreground">{c.sector}</p></div>
                    <span className="px-2.5 py-1 rounded-lg bg-emerald-500/15 text-emerald-500 text-sm font-bold">{c.roas}</span>
                  </div>
                  <div className="flex gap-0.5 mb-2">{[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-amber-400 text-amber-400" />)}</div>
                  <p className="text-sm text-muted-foreground italic">« {c.quote} »</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARATOR */}
      <section className="px-6 pb-8">
        <div className="max-w-5xl mx-auto mg-glass rounded-3xl p-8 sm:p-12">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-center mb-10">Agence traditionnelle vs Monaga Adflow</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-border p-6">
              <p className="font-semibold text-muted-foreground mb-4">Agence traditionnelle</p>
              {["Délais de 2 à 4 semaines", "Coûts élevés par création", "Peu de variantes testées", "Diffusion manuelle"].map((t) => (
                <p key={t} className="flex items-center gap-2 text-sm py-1.5 text-muted-foreground"><span className="text-destructive">✕</span>{t}</p>
              ))}
            </div>
            <div className="rounded-2xl border border-primary/40 bg-primary/5 p-6 mg-glow">
              <p className="font-semibold mb-4 mg-gradient-text">Monaga Adflow</p>
              {["Créations en quelques secondes", "Crédits IA abordables", "Variantes illimitées en 1 clic", "Diffusion multi-réseaux automatisée"].map((t) => (
                <p key={t} className="flex items-center gap-2 text-sm py-1.5"><Check size={16} className="text-emerald-500" />{t}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="px-6 py-24">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold tracking-widest uppercase text-primary">FAQ intelligente</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3">Questions fréquentes</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-border rounded-2xl px-5 bg-card" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-medium hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 pb-24">
        <div className="max-w-5xl mx-auto rounded-3xl p-10 sm:p-16 text-center relative overflow-hidden bg-gradient-to-br from-indigo-600 via-indigo-700 to-cyan-600">
          <div className="absolute inset-0 mg-grid-bg opacity-20" />
          <div className="relative">
            <Zap size={40} className="text-white mx-auto mb-4" />
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-white mb-4">Prêt à multiplier vos ventes ?</h2>
            <p className="text-white/80 max-w-xl mx-auto mb-8">Rejoignez les marques qui créent et diffusent leurs publicités e-commerce 10x plus vite avec l'IA.</p>
            <Button onClick={() => navigate("/register")} className="h-12 px-8 rounded-xl text-base bg-white text-indigo-700 hover:bg-white/90 border-0" data-testid="cta-bottom-register">
              Démarrer gratuitement <ArrowRight size={18} className="ml-1" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
